import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, ApiError } from '../../api'
import { useI18n } from '../../i18n'
import type { Demand, JobRole } from '../../types'
import { addDays, formatHourRange, formatLong, parseYmd, todayYmd, ymd } from '../../lib/dates'
import { Empty, Modal, Spinner, useConfirm, useToast } from '../../components/ui'
import { IconPlus, IconTrash } from '../../components/icons'

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const NEW_ROLE = '__new__'

// Tint a hex colour for use as a soft chip background.
function tint(hex: string): string {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim())
  if (!m) return 'var(--purple-soft)'
  const n = parseInt(m[1], 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  return `rgba(${r}, ${g}, ${b}, 0.16)`
}

function RoleChip({ name, color }: { name: string; color?: string | null }) {
  const c = color || '#843A84'
  return (
    <span className="badge" style={{ background: tint(c), color: c }}>
      {name}
    </span>
  )
}

export default function OpenTimes() {
  const toast = useToast()
  const confirm = useConfirm()
  const { t } = useI18n()
  const [demands, setDemands] = useState<Demand[]>([])
  const [roles, setRoles] = useState<JobRole[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Demand | null>(null)

  // create form
  const [date, setDate] = useState(todayYmd())
  const [start, setStart] = useState(9)
  const [end, setEnd] = useState(17)
  const [required, setRequired] = useState(1)
  const [role, setRole] = useState('')
  const [notes, setNotes] = useState('')
  const [title, setTitle] = useState('')
  const [roleId, setRoleId] = useState<string>('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const today = parseYmd(todayYmd())
    try {
      const [d, r] = await Promise.all([
        api.demands(ymd(addDays(today, -7)), ymd(addDays(today, 180))),
        api.roles(),
      ])
      setDemands(d.demands)
      setRoles(r.roles)
    } catch {
      toast.push(t('ot.loadErr'), 'error')
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => {
    load()
  }, [load])

  // Inline "+ New role…" — prompt for a name, create it, return the new id.
  const createRoleInline = useCallback(async (): Promise<string | null> => {
    const name = window.prompt(t('roles.newPrompt'))?.trim()
    if (!name) return null
    try {
      const { role } = await api.createRole({ name })
      setRoles((list) => [...list, role].sort((a, b) => a.name.localeCompare(b.name)))
      return role.id
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('roles.createErr'), 'error')
      return null
    }
  }, [toast, t])

  async function create(e: FormEvent) {
    e.preventDefault()
    if (end <= start) {
      toast.push(t('ot.endAfterStart'), 'error')
      return
    }
    setBusy(true)
    try {
      const { demand } = await api.createDemand({
        date,
        start_hour: start,
        end_hour: end,
        required,
        role: role.trim(),
        notes: notes.trim(),
        title: title.trim(),
        role_id: roleId || null,
      })
      setDemands((list) => [...list, demand])
      setRole('')
      setNotes('')
      setTitle('')
      setRoleId('')
      toast.push(t('ot.toast.opened'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('ot.openErr'), 'error')
    } finally {
      setBusy(false)
    }
  }

  async function onRoleSelect(value: string, set: (id: string) => void) {
    if (value === NEW_ROLE) {
      const id = await createRoleInline()
      if (id) set(id)
    } else {
      set(value)
    }
  }

  async function remove(d: Demand) {
    if (!(await confirm({ body: t('ot.removeConfirm') }))) return
    try {
      await api.deleteDemand(d.id)
      setDemands((list) => list.filter((x) => x.id !== d.id))
      toast.push(t('ot.toast.removed'), 'ok')
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : t('ot.removeErr'), 'error')
    }
  }

  const grouped = useMemo(() => {
    const map: Record<string, Demand[]> = {}
    for (const d of [...demands].sort((a, b) => (a.date + a.start_hour).localeCompare(b.date + String(b.start_hour)))) {
      ;(map[d.date] ||= []).push(d)
    }
    return map
  }, [demands])

  const dates = Object.keys(grouped).sort()

  return (
    <div>
      <div className="page-head">
        <h1>{t('ot.title')}</h1>
        <p>{t('ot.subtitle')}</p>
      </div>

      <div className="grid cols-2">
        <div className="card pad-lg" style={{ alignSelf: 'start' }}>
          <h3 style={{ marginBottom: 16 }}>{t('ot.newWindow')}</h3>
          <form className="auth-form" onSubmit={create}>
            <div className="field">
              <label>{t('ot.date')}</label>
              <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
            <div className="row gap-16">
              <div className="field grow">
                <label>{t('ot.from')}</label>
                <select className="input" value={start} onChange={(e) => setStart(Number(e.target.value))}>
                  {HOURS.map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
              <div className="field grow">
                <label>{t('ot.to')}</label>
                <select className="input" value={end} onChange={(e) => setEnd(Number(e.target.value))}>
                  {HOURS.slice(1).concat(24).map((h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ width: 110 }}>
                <label>{t('ot.perHour')}</label>
                <input
                  className="input"
                  type="number"
                  min={1}
                  value={required}
                  onChange={(e) => setRequired(Math.max(1, Number(e.target.value)))}
                />
              </div>
            </div>
            <div className="field">
              <label>{t('ot.eventName')} <span className="muted">({t('common.optional')})</span></label>
              <input className="input" type="text" placeholder={t('ot.eventNamePlaceholder')} value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="field">
              <label>{t('roles.label')} <span className="muted">({t('common.optional')})</span></label>
              <select className="input" value={roleId} onChange={(e) => onRoleSelect(e.target.value, setRoleId)}>
                <option value="">{t('roles.none')}</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
                <option value={NEW_ROLE}>{t('roles.new')}</option>
              </select>
            </div>
            <div className="field">
              <label>{t('ot.roleLabel')} <span className="muted">({t('common.optional')})</span></label>
              <input className="input" type="text" placeholder={t('ot.rolePlaceholder')} value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
            <div className="field">
              <label>{t('ot.notes')} <span className="muted">({t('common.optional')})</span></label>
              <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('ot.notesPlaceholder')} />
            </div>
            <button className="btn btn-primary btn-lg" type="submit" disabled={busy}>
              {busy ? <Spinner /> : <><IconPlus width={18} height={18} /> {t('ot.openBtn')}</>}
            </button>
          </form>
        </div>

        <div className="col gap-16">
          {loading ? (
            <div className="card" style={{ display: 'grid', placeItems: 'center', minHeight: 160 }}>
              <Spinner large />
            </div>
          ) : dates.length === 0 ? (
            <div className="card">
              <Empty title={t('ot.empty.title')}>
                <p className="muted">{t('ot.empty.hint')}</p>
              </Empty>
            </div>
          ) : (
            dates.map((d) => (
              <div className="card" key={d}>
                <div className="card-head">
                  <h3>{formatLong(d)}</h3>
                  <span className="tag">
                    {grouped[d].length === 1 ? t('ot.window1') : t('ot.windows', { n: grouped[d].length })}
                  </span>
                </div>
                <div className="list">
                  {grouped[d].map((dem) => (
                    <div className="list-row" key={dem.id}>
                      <div className="grow">
                        <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
                          {dem.title && <span className="title" style={{ fontWeight: 800 }}>{dem.title}</span>}
                          {dem.role_name && <RoleChip name={dem.role_name} color={dem.role_color} />}
                        </div>
                        <div className="title">
                          {formatHourRange(dem.start_hour, dem.end_hour)} · {t('ot.perHourLabel', { n: dem.required })}
                        </div>
                        <div className="sub">
                          {[dem.role, dem.notes].filter(Boolean).join(' — ') || t('ot.noLabel')}
                        </div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditing(dem)}>
                        {t('ot.edit')}
                      </button>
                      <button className="iconbtn" title={t('day.remove')} onClick={() => remove(dem)}>
                        <IconTrash width={17} height={17} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {editing && (
        <EditDemandModal
          demand={editing}
          roles={roles}
          onCreateRole={createRoleInline}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            setDemands((list) => list.map((x) => (x.id === updated.id ? updated : x)))
            setEditing(null)
            toast.push(t('ot.toast.updated'), 'ok')
          }}
        />
      )}
    </div>
  )
}

function EditDemandModal({
  demand,
  roles,
  onCreateRole,
  onClose,
  onSaved,
}: {
  demand: Demand
  roles: JobRole[]
  onCreateRole: () => Promise<string | null>
  onClose: () => void
  onSaved: (d: Demand) => void
}) {
  const { t } = useI18n()
  const [start, setStart] = useState(demand.start_hour)
  const [end, setEnd] = useState(demand.end_hour)
  const [required, setRequired] = useState(demand.required)
  const [role, setRole] = useState(demand.role)
  const [notes, setNotes] = useState(demand.notes)
  const [title, setTitle] = useState(demand.title ?? '')
  const [roleId, setRoleId] = useState<string>(demand.role_id ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function onRoleSelect(value: string) {
    if (value === NEW_ROLE) {
      const id = await onCreateRole()
      if (id) setRoleId(id)
    } else {
      setRoleId(value)
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault()
    if (end <= start) {
      setError(t('ot.endAfterStart'))
      return
    }
    setBusy(true)
    try {
      const { demand: updated } = await api.updateDemand(demand.id, {
        start_hour: start,
        end_hour: end,
        required,
        role: role.trim(),
        notes: notes.trim(),
        title: title.trim(),
        role_id: roleId || null,
      })
      onSaved(updated)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('ot.saveErr'))
      setBusy(false)
    }
  }

  return (
    <Modal title={t('ot.edit.title')} subtitle={formatLong(demand.date)} onClose={onClose}>
      <form className="auth-form" onSubmit={save}>
        {error && <div className="alert error">{error}</div>}
        <div className="row gap-16">
          <div className="field grow">
            <label>{t('ot.from')}</label>
            <select className="input" value={start} onChange={(e) => setStart(Number(e.target.value))}>
              {HOURS.map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div className="field grow">
            <label>{t('ot.to')}</label>
            <select className="input" value={end} onChange={(e) => setEnd(Number(e.target.value))}>
              {HOURS.slice(1).concat(24).map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ width: 110 }}>
            <label>{t('ot.perHour')}</label>
            <input
              className="input"
              type="number"
              min={1}
              value={required}
              onChange={(e) => setRequired(Math.max(1, Number(e.target.value)))}
            />
          </div>
        </div>
        <div className="field">
          <label>{t('ot.eventName')}</label>
          <input className="input" type="text" placeholder={t('ot.eventNamePlaceholder')} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field">
          <label>{t('roles.label')}</label>
          <select className="input" value={roleId} onChange={(e) => onRoleSelect(e.target.value)}>
            <option value="">{t('roles.none')}</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
            <option value={NEW_ROLE}>{t('roles.new')}</option>
          </select>
        </div>
        <div className="field">
          <label>{t('ot.roleLabel')}</label>
          <input className="input" type="text" value={role} onChange={(e) => setRole(e.target.value)} />
        </div>
        <div className="field">
          <label>{t('ot.notes')}</label>
          <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <Spinner /> : t('common.saveChanges')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
